import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ShopifyIntegrationCard = ({ user, token, shopifyConnectionStatus, selectedPlatform, onConnect, onDisconnect, showConfirmDialog = true }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [connections, setConnections] = useState([]);

  // Charger le statut de connexion au montage
  useEffect(() => {
    if (user && token) {
      loadShopifyStatus();
    }
  }, [user, token]);

  const loadShopifyStatus = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${BACKEND_URL}/api/shopify/status`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setConnections(response.data.active_shops || []);
      setError('');
      
      if (onConnect) {
        onConnect(response.data);
      }
      
    } catch (err) {
      console.error('Erreur lors du chargement du statut Shopify:', err);
      setError('Impossible de charger le statut Shopify');
      if (onConnect) {
        onConnect({ status: 'error', message: 'Erreur de chargement' });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDisconnectAll = async () => {
    if (showConfirmDialog && !window.confirm(
      `Êtes-vous sûr de vouloir déconnecter toutes vos boutiques Shopify (${connections.length}) ?`
    )) {
      return;
    }

    try {
      setLoading(true);
      await axios.post(
        `${BACKEND_URL}/api/shopify/disconnect`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setConnections([]);
      setError('');
      
      if (onDisconnect) {
        onDisconnect({ status: 'revoked', message: 'Boutiques déconnectées' });
      }

    } catch (err) {
      console.error('Erreur de déconnexion Shopify:', err);
      setError(err.response?.data?.detail || 'Erreur lors de la déconnexion');
    } finally {
      setLoading(false);
    }
  };

  const getConnectionStatus = () => {
    if (loading) return { status: 'loading', color: 'gray', text: 'Chargement...' };
    if (error) return { status: 'error', color: 'red', text: 'Erreur' };
    if (connections.length > 0) {
      return {
        status: 'connected',
        color: 'green',
        text: `${connections.length} boutique${connections.length > 1 ? 's' : ''} connectée${connections.length > 1 ? 's' : ''}`
      };
    }
    return { status: 'none', color: 'gray', text: 'Non connecté' };
  };

  const status = getConnectionStatus();

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="p-4 sm:p-6 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <span className="text-xl">🛍️</span>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Intégration Shopify
              </h3>
              <p className="text-sm text-gray-600">
                Connectez et gérez vos boutiques Shopify
              </p>
            </div>
          </div>
          
          {/* Status badge */}
          <div className={`px-3 py-1 rounded-full text-sm font-medium flex items-center gap-2 ${
            status.color === 'green' ? 'bg-green-100 text-green-700' :
            status.color === 'red' ? 'bg-red-100 text-red-700' :
            'bg-gray-100 text-gray-700'
          }`}>
            <div className={`w-2 h-2 rounded-full ${
              status.color === 'green' ? 'bg-green-500' :
              status.color === 'red' ? 'bg-red-500' :
              'bg-gray-500'
            }`}></div>
            {status.text}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 sm:p-6">
        {/* Error message */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        {/* Boutiques connectées */}
        {connections.length > 0 && (
          <div className="mb-4">
            <h4 className="font-medium text-gray-900 mb-3 text-sm">
              Boutiques connectées ({connections.length})
            </h4>
            <div className="space-y-2 max-h-32 overflow-y-auto">
              {connections.map((connection, index) => (
                <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded-md">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-lg flex-shrink-0">🏪</span>
                    <div className="min-w-0">
                      <p className="font-medium text-gray-900 text-sm truncate">
                        {connection.shop_name || connection.shop_domain}
                      </p>
                      <p className="text-xs text-gray-600 truncate">
                        {connection.shop_domain}
                      </p>
                    </div>
                  </div>
                  <span className="text-green-600 text-xs flex-shrink-0">✅ Actif</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="space-y-3">
          {/* Bouton principal - Gérer ou Connecter */}
          <button
            onClick={() => navigate('/integrations/shopify')}
            disabled={loading}
            className="w-full bg-green-500 hover:bg-green-600 disabled:bg-gray-300 text-white font-medium py-2.5 px-4 rounded-md transition-colors flex items-center justify-center gap-2 text-sm"
          >
            {loading ? (
              <>
                <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                Chargement...
              </>
            ) : connections.length > 0 ? (
              <>
                <span>⚙️</span>
                Gérer Shopify ({connections.length})
              </>
            ) : (
              <>
                <span>🔗</span>
                Connecter Shopify
              </>
            )}
          </button>

          {/* Bouton de déconnexion si connecté */}
          {connections.length > 0 && (
            <button
              onClick={handleDisconnectAll}
              disabled={loading}
              className="w-full bg-red-500 hover:bg-red-600 disabled:bg-gray-300 text-white font-medium py-2 px-4 rounded-md transition-colors flex items-center justify-center gap-2 text-sm"
            >
              {loading ? (
                <>
                  <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                  Déconnexion...
                </>
              ) : (
                <>
                  <span>🔌</span>
                  Déconnecter ({connections.length})
                </>
              )}
            </button>
          )}
        </div>

        {/* Informations sur les capacités */}
        <div className="mt-4 p-3 bg-green-50 rounded-lg border border-green-200">
          <h4 className="font-medium text-green-900 mb-2 text-sm">🚀 Capacités Phase 1</h4>
          <ul className="text-xs text-green-800 space-y-1">
            <li>• Connexion OAuth 2.0 sécurisée</li>
            <li>• Multi-boutiques supportées</li>
            <li>• Tokens chiffrés AES-GCM</li>
            <li>• Gestion centralisée des connexions</li>
          </ul>
        </div>

        {/* Prochaines phases */}
        <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
          <h4 className="font-medium text-blue-900 mb-2 text-sm">🔮 Phases suivantes</h4>
          <div className="grid grid-cols-2 gap-2 text-xs text-blue-800">
            <div>
              <p className="font-medium">Phase 2 - SEO</p>
              <p>Optimisation automatique</p>
            </div>
            <div>
              <p className="font-medium">Phase 3 - Pricing</p>
              <p>Gestion des prix intelligente</p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer avec lien direct */}
      <div className="px-4 sm:px-6 py-3 bg-gray-50 border-t border-gray-100">
        <div className="flex items-center justify-between text-xs text-gray-600">
          <span>Phase 1 - Connexions</span>
          <button
            onClick={() => navigate('/integrations/shopify')}
            className="text-green-600 hover:text-green-700 font-medium"
          >
            Ouvrir la page Shopify →
          </button>
        </div>
      </div>
    </div>
  );
};

export default ShopifyIntegrationCard;